import { useState, useEffect } from 'react';

/**
 * Network status information
 */
export interface NetworkStatus {
  /** Whether the browser is online */
  isOnline: boolean;
  /** Timestamp of the last status change */
  lastChanged: Date | null;
}

/**
 * useNetworkStatus Hook
 *
 * Monitors the browser's network connectivity status using the Navigator Online/Offline API.
 *
 * Features:
 * - Detects online/offline state changes
 * - Tracks timestamp of last status change
 * - Automatically cleans up event listeners
 *
 * @returns NetworkStatus object with current online state and last change timestamp
 *
 * @example
 * ```tsx
 * const { isOnline, lastChanged } = useNetworkStatus();
 *
 * if (!isOnline) {
 *   return <OfflineBanner />;
 * }
 * ```
 *
 * Note: navigator.onLine can be unreliable. It only detects when the browser
 * is definitely offline (e.g., airplane mode), but "online" doesn't guarantee
 * actual internet connectivity. For true connectivity checks, consider polling
 * a health endpoint.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Navigator/onLine
 */
export const useNetworkStatus = (): NetworkStatus => {
  const [networkStatus, setNetworkStatus] = useState<NetworkStatus>({
    isOnline: navigator.onLine,
    lastChanged: null,
  });

  useEffect(() => {
    /**
     * Handler for when browser goes online
     */
    const handleOnline = (): void => {
      setNetworkStatus({
        isOnline: true,
        lastChanged: new Date(),
      });
    };

    /**
     * Handler for when browser goes offline
     */
    const handleOffline = (): void => {
      setNetworkStatus({
        isOnline: false,
        lastChanged: new Date(),
      });
    };

    // Add event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Cleanup event listeners on unmount
    return (): void => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return networkStatus;
};
